class SupplyBuyboxFlavours extends HTMLElement {
  connectedCallback() {
    this.maxFlavours = parseInt(this.dataset.maxFlavours, 10) || 2;
    this.tiles = Array.from(this.querySelectorAll('[data-flavour-id]'));
    this.selections = new Map();

    this.tiles.forEach((tile) => {
      tile.addEventListener('click', () => this.toggleTile(tile));
      tile.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (tile.dataset.soldOut) return;
        event.preventDefault();
        this.toggleTile(tile);
      });
    });

    this.preselectFromUrl();
  }

  // ?flavour=<handle>[,<handle>] (a product id works too) pre-selects those
  // tiles, so a shopper who clicked Grape on a collection card lands here with
  // Grape already picked; data-preselect-flavour does the same for a flavour's
  // own URL. Listing cards build that link from the flavour's
  // pdp-link tag (snippets/linked-pdp-url.liquid, src/helpers/linkedPdp.ts).
  //
  // Deferred until supply-buybox-form has upgraded: it listens for
  // flavours-change on the container in its own connectedCallback, and both
  // scripts are deferred in document order, so firing synchronously here could
  // land before that listener exists and leave the price rows unsynced.
  preselectFromUrl() {
    let wanted = [];
    try {
      wanted = new URLSearchParams(window.location.search)
        .getAll('flavour')
        .flatMap((value) => value.split(','))
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
    } catch (e) {
      return;
    }
    // A flavour's own page (pdp-link tag, rendered as the container's page)
    // marks itself on the element so it lands pre-selected without a query.
    if (!wanted.length && this.dataset.preselectFlavour) {
      wanted = [this.dataset.preselectFlavour.trim().toLowerCase()];
    }
    if (!wanted.length) return;

    const tiles = wanted
      .map((key) =>
        this.tiles.find(
          (tile) =>
            (tile.dataset.flavourHandle || '').toLowerCase() === key ||
            String(tile.dataset.flavourId || '') === key
        )
      )
      .filter((tile) => tile && !tile.dataset.soldOut && !this.selections.has(tile.dataset.flavourId))
      .slice(0, this.maxFlavours);
    if (!tiles.length) return;

    const apply = () => tiles.forEach((tile) => this.toggleTile(tile));
    const formReady = window.customElements && customElements.whenDefined
      ? customElements.whenDefined('supply-buybox-form')
      : Promise.resolve();
    // Race with a short timeout so a page without the form still preselects.
    Promise.race([formReady, new Promise((resolve) => setTimeout(resolve, 1500))]).then(() =>
      setTimeout(apply, 0)
    );
  }

  toggleTile(tile) {
    if (tile.dataset.soldOut) return;
    const id = tile.dataset.flavourId;

    if (this.selections.has(id)) {
      this.selections.delete(id);
      this.renderTile(tile, false);
      this.emitChange();
      return;
    }

    if (this.selections.size >= this.maxFlavours) {
      // Rolling selection, not a hard cap: picking past max_flavours evicts
      // the OLDEST pick instead of blocking the new one. Map preserves
      // insertion order, so the first key is always the oldest.
      const oldestId = this.selections.keys().next().value;
      this.selections.delete(oldestId);
      const oldestTile = this.tiles.find((t) => t.dataset.flavourId === oldestId);
      if (oldestTile) this.renderTile(oldestTile, false);
    }

    // Quantity is always 1 here — the pack/tier the customer picks decides
    // how many of each selected flavour actually ship, not this picker.
    this.selections.set(id, 1);
    this.renderTile(tile, true);
    this.emitChange();
  }

  renderTile(tile, selected) {
    tile.classList.toggle('supply-buybox__flavour--selected', selected);
    tile.setAttribute('aria-pressed', String(selected));
  }

  emitChange() {
    const selections = Array.from(this.selections.entries()).map(([flavourId, quantity]) => {
      const tile = this.tiles.find((t) => t.dataset.flavourId === flavourId);
      return {
        flavourId,
        variantId: tile ? tile.dataset.variantId : null,
        quantity,
      };
    });

    const hiddenInput = this.querySelector('[data-flavour-selections]');
    if (hiddenInput) hiddenInput.value = JSON.stringify(selections);

    this.dispatchEvent(
      new CustomEvent('supply-buybox:flavours-change', {
        bubbles: true,
        detail: { selections, maxFlavours: this.maxFlavours },
      })
    );
  }
}

if (!customElements.get('supply-buybox-flavours')) {
  customElements.define('supply-buybox-flavours', SupplyBuyboxFlavours);
}
